/**
 * «Regjistrimi i Prezencës» — një bllok, i përdorur nga të gjithë ekranet.
 *
 * Ky seksion ekzistonte tri herë (profili, krijimi, regjistrimi) me të njëjtën
 * hierarki të rishkruar me dorë çdo herë. Rrjedha e njohur: dy prej tri
 * kopjeve rrëshqasin, dhe i njëjti çelës nis të lexohet si dy koncepte të
 * ndryshme në dy ekrane. Tani ka një kopje.
 *
 * ── Forma ────────────────────────────────────────────────────────────────
 * Varianti i mëparshëm i vinte kanalet njërin nën tjetrin, secili me etiketë,
 * shpjegim dhe çelësin e vet në rreshta të veçantë — nëntë rreshta teksti të
 * grumbulluar, që e shtynin kontratën jashtë ekranit. Kanalet janë tri
 * zgjedhje paralele, jo një listë e renditur, ndaj rrinë krah për krah si tri
 * pllaka; hollësia e secilit (skanimi, ora) rri BRENDA pllakës së vet, aty ku
 * i takon. Në `xs` pllakat mblidhen në një kolonë.
 *
 * ── Gjendjet që s'guxojnë të ngatërrohen ─────────────────────────────────
 * `channels === null` → «pa zgjedhje të vetën, ndiq kompaninë».
 * `channels === []`   → «asnjë kanal i lejuar», vendim i shprehur.
 * Të dyja duken ndryshe: e para si shënim neutral, e dyta si paralajmërim.
 *
 * ── Kontrolli ────────────────────────────────────────────────────────────
 * Komponenti nuk mban gjendje. Merr `value` dhe kthen `onChange(patch)` —
 * kështu e përdor edhe ekrani me `useState` (RegisterWorker) edhe ai me
 * react-hook-form (EditWorker), pa e detyruar asnjërin te forma e tjetrit.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Checkbox, FormControlLabel, Switch, TextField, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

/* ── Kanalet e regjistrimit të prezencës ──────────────────────────────────────
 * Pasqyron `backend/services/attendanceChannels.js`. Etiketat janë të njeriut;
 * çelësat janë ata që ruhen. Backend-i i rivalidon të gjitha — kjo listë
 * ekziston që admini të mos shkruajë kode me dorë. */
export const ATTENDANCE_CHANNELS = [
  { key: 'KIOSK',  label: 'Kiosk / QR',        hint: 'Pajisja e kompanisë te hyrja' },
  { key: 'WEB',    label: 'Web',               hint: 'Ora e punës te shiriti i sipërm' },
  { key: 'MOBILE', label: 'Aplikacioni mobil', hint: 'Telefoni i vetë punëtorit' },
];

/**
 * `null` ruhet kur punëtori s'ka konfigurim të vetin — ndiq kompaninë.
 * `[]` është vendim i shprehur: asnjë kanal. Të dyja janë gjendje të vërteta
 * dhe nuk guxojnë të rrafshohen në një.
 */
export function normalizeChannels(v) {
  if (v === null || v === undefined || v === '') return null;
  const list = Array.isArray(v) ? v : String(v).split(/[,\s]+/);
  const known = ATTENDANCE_CHANNELS.map((c) => c.key);
  const out = list.map((x) => String(x).trim().toUpperCase()).filter((x) => known.includes(x));
  return known.filter((k) => out.includes(k));
}

export default function AttendanceAccess({ value = {}, onChange, canManage = true }) {
  const theme = useTheme();
  const { t } = useTranslation();

  const enabled = value.enabled !== false;
  const channels = value.channels ?? null;
  const usesDefault = channels === null;
  const active = usesDefault ? [] : channels;
  const kioskOn = value.kioskScanAllowed !== false;
  const trackerOn = value.hasTimeTracker !== false;

  const set = (patch) => onChange && onChange(patch);

  const toggle = (key) => {
    // Prekja e parë e nxjerr punëtorin nga parazgjedhja e kompanisë dhe e bën
    // konfigurimin të tijin — nga ai çast vendos ky ekran, jo cilësimet.
    const base = usesDefault ? [] : active;
    const next = base.includes(key) ? base.filter((k) => k !== key) : [...base, key];
    set({ channels: normalizeChannels(next) || [] });
  };

  const captionSx = { fontSize: '0.6875rem', lineHeight: 1.35, color: 'text.secondary' };

  return (
    <Box sx={{
      borderRadius: 1.5,
      border: '1px solid',
      borderColor: enabled ? 'divider' : 'error.light',
      bgcolor: 'background.paper',
      overflow: 'hidden',
    }}>
      {/* Koka — e drejta, e thënë një herë. Çelësi rri majtas sepse gjithçka
          poshtë varet prej tij; djathtas do të lexohej si veprim i veçuar. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.75 }}>
        <Switch
          size="small"
          checked={enabled}
          disabled={!canManage}
          onChange={(e) => set({ enabled: e.target.checked })}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.3 }}>
            {t('worker.attendanceEnabled', 'Regjistrimi i Prezencës')}
          </Typography>
          <Typography sx={captionSx}>
            {t('worker.attendanceEnabledHint', 'Kur çkyçet, punëtori nuk bën Check-in/Check-out nga asnjë kanal. Orari i punës nuk e prek këtë të drejtë.')}
          </Typography>
        </Box>
      </Box>

      {/* Kanalet dalin vetëm kur çelësi kryesor është i ndezur: me prezencën e
          fikur, një listë kanalesh do të premtonte një zgjedhje që s'ekziston. */}
      {enabled && (
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', px: 1.25, py: 1 }}>
          {/* Etiketa e grupit dhe gjendja e tij në një rresht të vetëm. Teksti
              «pa zgjedhje të vetën» rrinte poshtë si rresht i katërt; këtu
              lexohet bashkë me titullin që e shpjegon. */}
          <Box sx={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            gap: 1, flexWrap: 'wrap', mb: 0.75,
          }}>
            <Typography sx={{
              fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em',
              textTransform: 'uppercase', color: 'text.secondary',
            }}>
              {t('worker.attendanceChannels', 'Kanalet e lejuara')}
            </Typography>
            {usesDefault ? (
              <Typography sx={{ ...captionSx, textAlign: 'right' }}>
                {t('worker.attendanceChannelsDefault', 'Pa zgjedhje të vetën — ndiqet parazgjedhja e kompanisë te Konfigurimi.')}
              </Typography>
            ) : active.length === 0 && (
              <Typography sx={{ ...captionSx, color: 'error.main', fontWeight: 600, textAlign: 'right' }}>
                {t('worker.attendanceChannelsNone', 'Asnjë kanal i zgjedhur — punëtori nuk regjistron dot prezencë.')}
              </Typography>
            )}
          </Box>

          {!canManage && (
            <Typography sx={{ ...captionSx, color: 'warning.onSoft', fontWeight: 600, mb: 0.75 }}>
              {t('worker.attendanceChannelsLocked', 'Vetëm WFM (attendance.manage) i ndryshon kanalet — serveri e refuzon çdo ndryshim tjetër.')}
            </Typography>
          )}

          {/* Tri zgjedhje paralele → tri pllaka krah për krah. Në `xs` mblidhen. */}
          <Box sx={{
            display: 'grid',
            gap: 0.75,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
          }}>
            {ATTENDANCE_CHANNELS.map((c) => {
              const on = active.includes(c.key);
              // Hollësia shfaqet edhe kur punëtori ende ndjek parazgjedhjen e
              // kompanisë (ku KIOSK dhe WEB janë të ndezur) — pa atë kusht,
              // punëtori i pakonfiguruar, pra shumica, do t'i kishte këto
              // kontrolle të paarritshme.
              const showDetail = on || usesDefault;
              return (
                <Box
                  key={c.key}
                  sx={{
                    borderRadius: 1.25,
                    border: '1px solid',
                    borderColor: on ? 'primary.main' : 'divider',
                    bgcolor: on ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                    transition: `border-color ${theme.motion?.fast ?? 160}ms, background-color ${theme.motion?.fast ?? 160}ms`,
                    px: 0.75, py: 0.5,
                    minWidth: 0,
                  }}
                >
                  {/* Etiketa mbështjell kutinë e zgjedhjes: klikimi mbi emrin e
                      kanalit e ndez atë, si te çdo formë. */}
                  <FormControlLabel
                    sx={{ m: 0, alignItems: 'flex-start', width: '100%' }}
                    control={(
                      <Checkbox
                        size="small"
                        checked={on}
                        disabled={!canManage}
                        onChange={() => toggle(c.key)}
                        sx={{ p: 0.5, mt: -0.25 }}
                      />
                    )}
                    label={(
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', lineHeight: 1.3 }}>
                          {c.label}
                        </Typography>
                        <Typography sx={captionSx}>{c.hint}</Typography>
                      </Box>
                    )}
                  />

                  {showDetail && c.key === 'KIOSK' && (
                    <Box sx={{ pl: 0.5, pt: 0.25 }}>
                      <FormControlLabel
                        sx={{ m: 0, alignItems: 'center' }}
                        control={(
                          <Switch
                            size="small"
                            checked={kioskOn}
                            disabled={!canManage}
                            onChange={(e) => set({ kioskScanAllowed: e.target.checked })}
                          />
                        )}
                        label={(
                          <Typography sx={{
                            ...captionSx,
                            fontWeight: 600,
                            color: kioskOn ? 'text.secondary' : 'error.main',
                          }}>
                            {kioskOn
                              ? t('worker.kioskScanOn', 'Skanimi i lejuar')
                              : t('worker.kioskScanSuspended', 'Skanimi i pezulluar')}
                          </Typography>
                        )}
                      />
                      {/* Arsyeja shfaqet vetëm kur skanimi është i pezulluar —
                          është teksti që sheh vetë punëtori te kioska. */}
                      {!kioskOn && (
                        <TextField
                          size="small"
                          fullWidth
                          value={value.kioskScanNote || ''}
                          disabled={!canManage}
                          onChange={(e) => set({ kioskScanNote: e.target.value })}
                          sx={{ mt: 0.5, '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 } }}
                          placeholder={t('worker.kioskScanNote', 'Arsyeja që sheh punëtori — p.sh. “Pezulluar deri më 20 gusht”')}
                          inputProps={{ maxLength: 120 }}
                        />
                      )}
                    </Box>
                  )}

                  {showDetail && c.key === 'WEB' && (
                    <Box sx={{ pl: 0.5, pt: 0.25 }}>
                      <FormControlLabel
                        sx={{ m: 0, alignItems: 'center' }}
                        control={(
                          <Switch
                            size="small"
                            checked={trackerOn}
                            disabled={!canManage}
                            onChange={(e) => set({ hasTimeTracker: e.target.checked })}
                          />
                        )}
                        label={(
                          <Typography sx={{ ...captionSx, fontWeight: 600 }}>
                            {t('worker.timeTracker', 'Shfaq orën te shiriti i sipërm')}
                          </Typography>
                        )}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}
